import React, { useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// COMPONENTS
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import HubContainer from "@/main/components/shared/hub/HubContainer";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import Button from "@/main/components/shared/button/button";

// UTILS/SERVICES/API
import { procurementApi } from "@/main/api";
import {
	GetRequoteProcurementItemsRequest,
	ReasonFilterRequest,
	RequotePartProcurementRequest,
} from "@/main/models/request";
import {
	RequoteProcurementItemResponse,
	RequoteProcurementResponse,
	RequoteProcurementSupplierResponse,
} from "@/main/models/response";
import {
	getDefaultCountryOptionDrop,
	getTranslateByKey,
	isEmpty,
	isNullUndefined,
	isShowControlByElementId,
} from "@/main/services/utils";
import { KeyValueDefault } from "@/main/models/common";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { convertStringForBoolean } from "@/main/services/utilsString";
import { getReason, useReasonReducer } from "@/main/store/reason";
import { SendUpdatesSignalRTypeEnum } from "@/main/services/enum/SendUpdatesSignalRTypeEnum";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";
import { initalDateForm } from "@/main/services/utilsDate";
import { signalRClearReceiveUpdatesByKey } from "@/main/store/signalR";
import { Controls } from "@/main/services/constants/constantsControl";
import AreaDeadlineAnswerRequote from "@/main/components/shared/areaDeadlineAnswerRequote/areaDeadlineAnswerRequote";
import { getPartTypesByDirectAssignCriteria } from "@/main/store/partType";

// LOCAL STYLES & COMPONENTS
import RowSupplierRequoteOrdered from "./rowSupplierRequoteOrdered/rowSupplierRequoteOrdered";
import ModalConfirmRequoteOrdered from "../modalConfirmRequoteOrdered/modalConfirmRequoteOrdered";
import * as S from "./styles";
import { getRefreshForceUrl } from "@/main/services/utilsQueryParams";

export interface IItemFormRequoteOrdered extends RequoteProcurementItemResponse {
	partType: KeyValueDefault | null;
	isSelected: boolean;
}
export interface IItemFormRequoteSupplierOrdered extends RequoteProcurementSupplierResponse {
	items: IItemFormRequoteOrdered[];
}

const INITIAL_FORM_STATE = {
	reason: null as KeyValueDefault | null,
	reasonDescription: "",
	deadlineAnswer: 0 as number,
	isNational: false as boolean,
	suppliers: [] as IItemFormRequoteSupplierOrdered[],
};

export type IRequoteOrderedForm = typeof INITIAL_FORM_STATE;
interface IModalRequoteOrderedPartProps {
	isOpenModal: boolean;
	orderNr: string;
	claimNr: string | undefined;
	guidQuote: string;
	guidProcurement: string;
	setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	closeModal: () => void;
}
function ModalRequoteOrderedPart({
	isOpenModal,
	claimNr,
	guidQuote,
	guidProcurement,
	setIsOpenModal,
	closeModal,
}: IModalRequoteOrderedPartProps) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const parameterRangeDays = useParameterByTypeScreenReducer(ParameterTypeEnum.DefaultSearchOrderDaysInterval);
	const showDeadlineAnswerInput = isShowControlByElementId(Controls.IdRequoteAnswerProcurementTime);
	const showRequoteNational = isShowControlByElementId(Controls.IdRequoteNationalProcurement);

	//#region Reducers/States
	const { reason: reasonReducer } = useReasonReducer();
	const { countriesUser: countryReducer } = useCountryReducer();

	const [isLoadingGet, setIsLoadingGet] = useState<boolean>(false);
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
	const [isLoadingSignalR, setIsLoadingSignalR] = useState<boolean>(false);
	const [isErrorSignalR, setIsErrorSignalR] = useState<boolean>(false);
	const [isOpenModalConfirm, setIsOpenModalConfirm] = useState<boolean>(false);
	const [dataRequoteOrderedPart, setDataRequoteOrderedPart] = useState<RequoteProcurementResponse | null>(null);

	let initialDeadline = dataRequoteOrderedPart?.paramSupplierDeadlineAnswer || 0;
	let minDeadline = dataRequoteOrderedPart?.paramMinCustomDeadlineAnswer || 30;
	let maxDeadline = dataRequoteOrderedPart?.ParamMaxCustomDeadlineAnswer || 300;
	if (initialDeadline < minDeadline) initialDeadline = minDeadline;
	if (initialDeadline > maxDeadline) initialDeadline = maxDeadline;

	INITIAL_FORM_STATE.deadlineAnswer = initialDeadline;

	const methods = useForm<IRequoteOrderedForm>({
		defaultValues: INITIAL_FORM_STATE,
	});

	const { control, getValues, setValue, handleSubmit, watch, trigger } = methods;

	const { fields, replace } = useFieldArray({ control, name: "suppliers" });
	const watcherReasonType = watch("reason");
	const isReasonOther = convertStringForBoolean(watcherReasonType?.default);
	//#endregion

	//#region Core functions

	function validateForm(data: IRequoteOrderedForm) {
		if (isNullUndefined(data.reason)) {
			toast.error(
				getTranslateByKey("information.selectOne", true).replace(
					"{0}",
					getTranslateByKey("information.reason").toLowerCase(),
				),
			);
			return false;
		}

		const hasOneSelected = data.suppliers.some((supplier) => supplier.items.some((item) => item.isSelected));
		if (!hasOneSelected) {
			toast.error(
				getTranslateByKey("information.selectOne", true).replace(
					"{0}",
					getTranslateByKey("information.part").toLowerCase(),
				),
			);
			return false;
		}
		if (showDeadlineAnswerInput) {
			if (!data.deadlineAnswer || data.deadlineAnswer < minDeadline || data.deadlineAnswer > maxDeadline) {
				const messageError = getTranslateByKey("quote.requoteDeadlineAnswerInvalid", true)
					.replace("{0}", minDeadline.toString())
					.replace("{1}", maxDeadline.toString())
					.replace("{2}", (data.deadlineAnswer || "").toString());
				toast.error(messageError);
				return false;
			}
		}
		return true;
	}

	function onFinishedRequote() {
		if (isLoadingSignalR) {
			const filters = getRefreshForceUrl(
				isEmpty(claimNr) ? { startDate: initalDateForm(parameterRangeDays) } : { quoteFilterVehicle: claimNr },
			);

			const routeRedirect = `${RoutesConstants.orderSearchOrder}${filters}`;
			navigate(routeRedirect);
			setIsLoadingSignalR(false);
			setIsOpenModal(false);
		}
	}

	function onErrorRequote() {
		setIsLoadingSignalR(false);
		setIsErrorSignalR(true);
	}

	useEffect(() => {
		if (!isOpenModal && isErrorSignalR) {
			dispatch(signalRClearReceiveUpdatesByKey(SendUpdatesSignalRTypeEnum.RequoteProcurement));
			setIsErrorSignalR(false);
			setIsLoadingSignalR(false);
		}
	}, [dispatch, isErrorSignalR, isOpenModal]);

	async function submit() {
		const isValid = await trigger();

		if (isValid) {
			setValue("isNational", false);
			handleSubmit(openModalConfirmation)();
		}
	}

	async function submitNational() {
		const isValid = await trigger();

		if (isValid) {
			setValue("isNational", true);
			handleSubmit(openModalConfirmation)();
		}
	}

	function addItemsToForm(result: RequoteProcurementResponse) {
		const suppliers = result.suppliers.map((supplier) => {
			const items = supplier.items.map((item) => ({
				...item,
				partType: isEmpty(item.idPartType)
					? null
					: { key: item.idPartType, value: "", default: String(item.idPartType) },
				isSelected: !item.canceledBySupplier,
			}));
			return {
				...supplier,
				items,
			};
		});

		replace(suppliers);
	}

	function getAllOptionsSelected(data: IItemFormRequoteSupplierOrdered[]) {
		const allOptionsSelected: IItemFormRequoteOrdered[] = [];

		data.forEach((supplier) => {
			supplier.items.forEach((item) => {
				if (item.isSelected) {
					allOptionsSelected.push(item);
				}
			});
		});

		return allOptionsSelected;
	}
	//#endregion

	//#region Async functions
	async function getRequoteOrderedPart() {
		if (isNullUndefined(guidProcurement)) return;
		if (isLoadingGet) return;

		setIsLoadingGet(true);
		const request: GetRequoteProcurementItemsRequest = {
			guidProcurement,
		};
		procurementApi
			.getRequoteProcurementItems(request)
			.then((response) => {
				const {
					data: { result },
				} = response;
				addItemsToForm(result);
				setDataRequoteOrderedPart(result);
			})
			.finally(() => {
				setIsLoadingGet(false);
			});
	}

	async function openModalConfirmation(data: IRequoteOrderedForm) {
		if (!validateForm(data)) return;
		setIsOpenModalConfirm(true);
	}

	async function onSubmitSave(data: IRequoteOrderedForm) {
		if (!validateForm(data)) return;
		setIsLoadingSave(true);
		setIsErrorSignalR(false);

		dispatch(signalRClearReceiveUpdatesByKey(SendUpdatesSignalRTypeEnum.RequoteProcurement));

		const isReplacePartTpye = isShowControlByElementId(Controls.IdSearchProcurementRequotePartType);
		const allOptionsSelected = getAllOptionsSelected(data.suppliers);
		const formattedRequoteItems = allOptionsSelected.map((item) => ({
			guidProcurementSupplierItem: item.procurementSupplierItemGuid,
			idPartType: isEmpty(item.partType?.default) ? null : Number(item.partType?.default),
			replacePartType: isReplacePartTpye,
		}));

		const request: RequotePartProcurementRequest = {
			guidProcurement,
			guidReason: String(data.reason?.key),
			reasonDescription: data.reasonDescription,
			isNational: data.isNational,
			requoteItems: formattedRequoteItems,
		};

		if (showDeadlineAnswerInput) {
			request.deadlineAnswerRequote = data.deadlineAnswer;
		}

		procurementApi
			.requotePartProcurement(request)
			.then(() => {})
			.finally(() => {
				setIsLoadingSave(false);
				setIsLoadingSignalR(true);
			});
	}
	//#endregion

	//#region UseEffects
	useEffect(() => {
		if (isOpenModal) {
			getRequoteOrderedPart();
			if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
			dispatch(getPartTypesByDirectAssignCriteria({ guidQuote }));
		}
	}, [isOpenModal]);

	useEffect(() => {
		const countryUser = getDefaultCountryOptionDrop(countryReducer.data);
		if (countryUser === null) return;

		const requestReason: ReasonFilterRequest = {
			guidCountry: String(countryUser.key),
			idReasonType: ReasonTypeEnum.RequoteProcurement,
		};
		dispatch(getReason(requestReason));
	}, [JSON.stringify(countryReducer.data)]);

	useEffect(() => {
		if (!isNullUndefined(dataRequoteOrderedPart)) {
			initialDeadline = dataRequoteOrderedPart?.paramSupplierDeadlineAnswer || 0;
			minDeadline = dataRequoteOrderedPart?.paramMinCustomDeadlineAnswer || 30;
			maxDeadline = dataRequoteOrderedPart?.ParamMaxCustomDeadlineAnswer || 300;
			if (initialDeadline < minDeadline) initialDeadline = minDeadline;
			if (initialDeadline > maxDeadline) initialDeadline = maxDeadline;
			setValue("deadlineAnswer", initialDeadline);
		}
	}, [dataRequoteOrderedPart]);
	//#endregion

	if (isLoadingGet) return <LoadingComponent />;
	if (isNullUndefined(dataRequoteOrderedPart) || isEmpty(dataRequoteOrderedPart.suppliers))
		return <ResultNotFoundComponent />;

	return (
		<FormProvider {...methods}>
			<form noValidate>
				<S.Container>
					<S.AreaSupplier>
						{!isLoadingSignalR &&
							fields.map((supplier, index) => (
								<RowSupplierRequoteOrdered key={supplier.id} indexSupplier={index} supplier={supplier} />
							))}
					</S.AreaSupplier>

					<S.AreaFooter>
						{isLoadingSignalR || isErrorSignalR ? (
							<HubContainer
								type={SendUpdatesSignalRTypeEnum.RequoteProcurement}
								customProps={{
									onFinishedRequote: onFinishedRequote,
									onErrorRequote: onErrorRequote,
								}}
							/>
						) : (
							<>
								{showDeadlineAnswerInput && (
									<AreaDeadlineAnswerRequote min={minDeadline} max={maxDeadline} control={control} />
								)}
								<S.AreaReason>
									<KogAutoCompleteControlled
										id="id-searchOrder-select-reason"
										nameCustom="reason"
										isDefaultValueUnique
										label={getTranslateByKey("information.reason")}
										control={control}
										rulesCustom={{ required: true }}
										setValueCustom={setValue}
										options={reasonReducer.data}
										isLoading={reasonReducer.isLoading}
										onChangeCustom={(option) => {
											if (option === null) return;

											if (convertStringForBoolean((option as KeyValueDefault)?.default)) {
												setValue("reasonDescription", "");
											}
										}}
									/>
									{isReasonOther && (
										<KogInputTextControlledNew
											required={isReasonOther}
											id="id-searchOrder-input-description"
											label={getTranslateByKey("information.description")}
											name="reasonDescription"
											control={control}
										/>
									)}
								</S.AreaReason>
							</>
						)}

						<S.AreaButtons>
							<SecondaryButton id="id-searchOrder-btn-requote-back" type="button" onClick={() => closeModal()}>
								{getTranslateByKey("information.back")}
							</SecondaryButton>
							{!isLoadingSignalR && (
								<>
									<Button id="id-searchOrder-btn-requote-save" isLoading={isLoadingSave} onClick={submit}>
										{getTranslateByKey("information.reQuote")}
									</Button>
									{showRequoteNational && (
										<Button
											id="id-searchOrder-btn-requote-national-save"
											isLoading={isLoadingSave}
											onClick={submitNational}
										>
											{getTranslateByKey("information.requoteNational")}
										</Button>
									)}
								</>
							)}
						</S.AreaButtons>
					</S.AreaFooter>
					<ModalConfirmRequoteOrdered
						openModalConfirm={isOpenModalConfirm}
						onClickActionConfirm={() => {
							onSubmitSave(getValues());
						}}
						setOpenModalConfirm={setIsOpenModalConfirm}
						callbackAfterRequotePart={() => {
							setIsOpenModalConfirm(false);
						}}
					/>
				</S.Container>
			</form>
		</FormProvider>
	);
}

function WrapperModalRequoteOrderedPart(props: IModalRequoteOrderedPartProps) {
	const titleModal = `${getTranslateByKey("reasonType.requoteProcurement")}: ${props.orderNr}`;
	return (
		<KogModalSideComponent shouldRenderOpen title={titleModal} open={props.isOpenModal} closeModal={props.closeModal}>
			<ModalRequoteOrderedPart {...props} />
		</KogModalSideComponent>
	);
}
export default WrapperModalRequoteOrderedPart;
